import { useCallback } from 'react'
import { Card, CardBody, Col, Row, Button } from 'reactstrap'

import { FIELD_NAMES } from 'components/BalanceOfPayments/types'
import type { CostArrayToString, ValidationType, BADGE_LABEL_TYPES } from 'components/BalanceOfPayments/types'
import { InputFormat, CustomButton } from 'components/common'
import { COLUMN_SIZES } from 'components/common/constants'

import { useBopFormInput } from 'hooks/useBopFormInput'
import useBopValidation from 'hooks/useBopValidation'

type BadgeLabelType = (typeof BADGE_LABEL_TYPES)[keyof typeof BADGE_LABEL_TYPES]

type FieldNameType = (typeof FIELD_NAMES)[keyof typeof FIELD_NAMES]

type FormInputProps = {
  costItems: CostArrayToString[]
  toFixed: boolean
  maxItems: number
  onChange: (val: (latest: CostArrayToString[]) => CostArrayToString[]) => void
  onBlur?: (value: string) => string | undefined
  onValidate: (isValid: boolean) => void
  titleText?: string
  addButtonText: string
  labelText: string
  BadgeLabelInput: BadgeLabelType
  addonText: string
  validations?: ValidationType
}

export const FormInput = ({
  costItems,
  toFixed,
  maxItems,
  onChange,
  onBlur,
  onValidate,
  titleText,
  addButtonText,
  labelText,
  BadgeLabelInput,
  addonText,
  validations,
}: FormInputProps) => {
  const { items, showAddButton, showAddSmallButton, nameValidations, costValidations } = useBopFormInput(
    costItems,
    onChange,
    validations,
    maxItems
  )
  const { updateValidationList } = useBopValidation(items, onValidate)
  const handleUpdateItem = useCallback(
    (index: number, field: FieldNameType, value: string | number) => {
      onChange(prev => prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item)))
    },
    [onChange]
  )

  const handleAddItem = useCallback(() => {
    const newItem = { name: '', cost: toFixed ? '0.00' : '0' }
    onChange(prev => [...prev, newItem])
  }, [onChange, toFixed])

  const handleDeleteItem = useCallback(
    (index: number) => {
      onChange(prev => prev.filter((_, idx) => idx !== index))
    },
    [onChange]
  )

  return (
    <>
      <CustomButton icon="plus" outline size="sm" onClick={handleAddItem} show={showAddButton}>
        {addButtonText}
      </CustomButton>
      {items.length ? (
        <>
          {titleText ? <div className="pt-2">{titleText}</div> : null}
          <div className="d-flex justify-content-end align-self-center mb-2 text-gray">
            {items.length} / {maxItems} 利用中
          </div>
          <Card>
            {items.map((item, index) => (
              <div key={index}>
                <CardBody>
                  <Row>
                    <Col xxl="9">
                      <InputFormat
                        label={labelText}
                        labelSize={COLUMN_SIZES.X_SHORT}
                        className="mb-3"
                        maxLength={30}
                        value={item.name}
                        onChange={value => handleUpdateItem(index, FIELD_NAMES.NAME, value)}
                        validations={nameValidations}
                        onValidate={isValid => updateValidationList(index, 'first', isValid)}
                      />
                      <BadgeLabelInput
                        label="金額※"
                        labelSize={COLUMN_SIZES.X_SHORT}
                        addonText={addonText}
                        maxLength={10}
                        value={item.cost}
                        onChange={value => handleUpdateItem(index, FIELD_NAMES.COST, value)}
                        onBlur={() => {
                          if (onBlur) {
                            const formattedValue = onBlur(item.cost)
                            if (formattedValue !== undefined) {
                              handleUpdateItem(index, FIELD_NAMES.COST, formattedValue)
                            }
                          }
                        }}
                        validations={costValidations}
                        onValidate={isValid => updateValidationList(index, 'second', isValid)}
                      />
                    </Col>
                    <Col xxl="3" className="d-flex justify-content-end align-items-center">
                      {index === items.length - 1 && (
                        <CustomButton
                          icon="plus"
                          outline
                          className="my-3 d-block text-nowrap"
                          onClick={handleAddItem}
                          show={showAddSmallButton}
                        >
                          追加
                        </CustomButton>
                      )}
                      <Button
                        outline
                        color="danger"
                        className="my-3 ms-2 d-block text-nowrap"
                        onClick={() => handleDeleteItem(index)}
                      >
                        削除
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
                {index < items.length - 1 && <hr className="m-0" />}
              </div>
            ))}
          </Card>
        </>
      ) : (
        <></>
      )}
    </>
  )
}
